import { Box, Paper, Typography, Button, Divider } from '@mui/material';
import { useContext, useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';

import NetworkCode from '../NetworkCode';
import GptTagModal from './GptTagModal';

import { UnitsContext } from '../../../../../Contexts/UnitsContext';

import {
	GAM_GPT_1X1_KEYS,
	SPLIT_DOC_PARAMETERS,
	SPLIT_DOC_EDIT_MODES
} from '../../../../../core/constants';

import {
	createSplitDocForSite,
	updateSplitDocForSite,
	syncSplitDocForSite
} from '../../../../../api/splitDoc';

import {
	removeGptTagFromSplitDoc,
	addGptTagToSplitDoc,
	getGPT1x1SplitTag,
	getGpt1x1InjectionScript,
	shouldUpdateGptTag
} from '../helpers';
import { isSplitDocValid } from '../../../../../utilities/splitDocConfigChecks';
import { ConfigContext } from '../../../../../Contexts/ConfigContext';
import FinalOverview from '../../../Components/FinalOverview';

const ScriptSplitNetworkCode = ({ splitDocNetworkCode, splitDocSuccessMessage, siteId }) => {
	const {
		splitDoc,
		splitDocEditMode,
		updateSplitDoc,
		getSplitDocWithEncodedScripts,
		getSplitDocWithDecodedScripts,
		previousSplitDoc,
		updateSplitDocEditMode
	} = useContext(UnitsContext);

	const { rawConfig, updateRawConfig } = useContext(ConfigContext);

	const existing1x1GptTag = splitDoc?.gpt1x1Config?.[rawConfig.videoSectionId] || null;

	const isSplitDocPresent = splitDocEditMode !== SPLIT_DOC_EDIT_MODES.CREATE;

	const [openModal, setOpenModal] = useState(false);
	const [tagType, setTagType] = useState(
		existing1x1GptTag?.type || GAM_GPT_1X1_KEYS.INSTREAM_GPT_1X1_TAG
	);
	const [tagSectionId, setTagSectionId] = useState(existing1x1GptTag?.sectionId || '');
	const [shouldSaveSplitDoc, setShouldSaveSplitDoc] = useState(false);

	const handleError = (err) => {
		toast.error(err?.response?.data?.data?.message || err?.message || 'Some Error Occurred', {
			position: toast.POSITION.BOTTOM_RIGHT
		});
	};

	useEffect(() => {
		if (!shouldSaveSplitDoc || !splitDoc) {
			return;
		}

		const saveSplitDoc = async () => {
			try {
				const finalDoc = getSplitDocWithEncodedScripts();
				isSplitDocValid(finalDoc);

				const payload = {
					data: finalDoc,
					previousCouchBaseConfig: previousSplitDoc.current,
					mode: splitDocEditMode,
					siteId: finalDoc.siteId,
					siteDomain: finalDoc.siteDomain,
					isInternalSplitDocRequest: true
				};

				let response = null;

				if (splitDocEditMode === SPLIT_DOC_EDIT_MODES.CREATE) {
					response = await createSplitDocForSite(payload);
				} else if (splitDocEditMode === SPLIT_DOC_EDIT_MODES.UPDATE) {
					response = await updateSplitDocForSite(payload);
				}

				const updatedDoc = response.data.data.data;

				previousSplitDoc.current = updatedDoc;
				updateSplitDocEditMode(SPLIT_DOC_EDIT_MODES.UPDATE);
				updateSplitDoc(getSplitDocWithDecodedScripts(updatedDoc));
				await syncSplitDocForSite({
					...payload,
					mode: SPLIT_DOC_EDIT_MODES.SYNC
				});

				toast.success('GPT 1x1 Tag applied successfully.', {
					position: toast.POSITION.BOTTOM_RIGHT
				});
			} catch (err) {
				handleError(err);
			} finally {
				setShouldSaveSplitDoc(false);
			}
		};

		saveSplitDoc();
	}, [shouldSaveSplitDoc]);

	useEffect(() => {
		if (openModal) {
			setTagType(existing1x1GptTag?.type || GAM_GPT_1X1_KEYS.INSTREAM_GPT_1X1_TAG);
			setTagSectionId(existing1x1GptTag?.sectionId || '');
		}
	}, [openModal]);

	const getFinalSectionId = () =>
		tagType === GAM_GPT_1X1_KEYS.INSTREAM_GPT_1X1_TAG ? rawConfig.videoSectionId : tagSectionId;

	const handleSaveTag = () => {
		try {
			if (shouldSaveSplitDoc) {
				return;
			}
			const finalSectionId = getFinalSectionId();

			if (!finalSectionId) {
				return toast.error('Tag section ID is required!', {
					position: toast.POSITION.BOTTOM_RIGHT
				});
			}

			let updatedDoc = structuredClone(splitDoc);
			if (!updatedDoc.gpt1x1Config) {
				updatedDoc.gpt1x1Config = {};
			}

			updatedDoc.gpt1x1Config[rawConfig.videoSectionId] = {
				type: tagType,
				sectionId: finalSectionId
			};
			updatedDoc[SPLIT_DOC_PARAMETERS.ENABLED] = true;

			if (shouldUpdateGptTag(updatedDoc)) {
				updatedDoc = removeGptTagFromSplitDoc(updatedDoc);
				const script = getGpt1x1InjectionScript(siteId);
				updatedDoc = addGptTagToSplitDoc(updatedDoc, script);
			}

			updateSplitDoc(updatedDoc);
			setShouldSaveSplitDoc(true);
			setOpenModal(false);
		} catch (err) {
			handleError(err);
		}
	};

	return (
		<Box sx={{ mt: 2 }}>
			<Paper sx={{ borderRadius: 2, p: 3 }}>
				<Box display="flex" justifyContent="space-between" alignItems="center" sx={{ my: 2 }}>
					<Box
						sx={{
							p: 1,
							borderRadius: 2,
							background: existing1x1GptTag?.sectionId ? '#e6f4ea' : '#fff7e6',
							border: `1px solid ${existing1x1GptTag?.sectionId ? '#b7eb8f' : '#ffe58f'}`
						}}
					>
						{existing1x1GptTag?.sectionId ? (
							<>
								<Typography variant="h7" sx={{ mb: 1, fontWeight: 'bold' }}>
									GPT 1x1 Tag Details
								</Typography>
								<Typography variant="body1">
									<strong>Tag Type:</strong> {existing1x1GptTag.type.toUpperCase()}
								</Typography>
								<Typography variant="body1">
									<strong>Section ID:</strong> {existing1x1GptTag.sectionId}
								</Typography>
							</>
						) : (
							'GPT 1x1 Tag is Not Configured'
						)}
					</Box>

					<Button variant="contained" color="error" onClick={() => setOpenModal(true)}>
						{existing1x1GptTag?.sectionId ? 'Edit 1x1 TAG' : 'CREATE 1x1 TAG'}
					</Button>
				</Box>

				<Divider sx={{ my: 2 }} />

				{/* CASE 1: SplitDoc not present */}
				{!isSplitDocPresent && (
					<Box sx={{ p: 2, background: '#fafafa', borderRadius: 1 }}>
						<Typography color="text.secondary">No script tag configuration found.</Typography>
					</Box>
				)}

				{/* CASE 2: SplitDoc present but GPT tag not present */}
				{isSplitDocPresent && !existing1x1GptTag?.sectionId && (
					<>
						<Typography variant="h7" sx={{ mb: 1 }}>
							Script Split Network Tag
						</Typography>
						<NetworkCode
							networkCode={splitDocNetworkCode}
							successMessage={splitDocSuccessMessage}
						/>
					</>
				)}

				{/* CASE 3: SplitDoc present and GPT tag also present */}
				{isSplitDocPresent && existing1x1GptTag?.sectionId && (
					<>
						<Typography variant="h7" sx={{ mb: 1 }}>
							GPT 1x1 Script Split Network Tag
						</Typography>

						<NetworkCode
							networkCode={getGPT1x1SplitTag(
								siteId,
								existing1x1GptTag.sectionId,
								existing1x1GptTag.type
							)}
							successMessage={splitDocSuccessMessage}
						/>
					</>
				)}
			</Paper>

			<GptTagModal
				open={openModal}
				onClose={() => setOpenModal(false)}
				onSave={handleSaveTag}
				tagType={tagType}
				setTagType={setTagType}
				tagSectionId={tagSectionId}
				setTagSectionId={setTagSectionId}
				videoSectionId={rawConfig.videoSectionId}
				existing1x1GptTag={existing1x1GptTag}
			/>
		</Box>
	);
};
export default ScriptSplitNetworkCode;
